import React, { useState, useRef, useEffect } from 'react';
import { colors } from '../../../styles/colors';

const TIMEZONE_LABELS = {
  'America/Los_Angeles': 'Pacific',
  'America/Denver': 'Mountain',
  'America/Chicago': 'Central',
  'America/New_York': 'Eastern',
};

const TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/New_York', label: 'Eastern (ET)' },
];

const RaceInformation = ({
  formData,
  handleInputChange,
  validateField,
  commonStyles
}) => {
  const onBlur = (fieldName) => {
    if (validateField) {
      validateField(fieldName);
    }
  };

  // Custom dropdown for Race Timezone — a native <select> takes over the full
  // screen on mobile browsers (OS-level picker), which looks jarring next to
  // the rest of the form. This keeps a consistent, same-sized dropdown on
  // both mobile and desktop instead of handing rendering off to the OS.
  const [tzOpen, setTzOpen] = useState(false);
  const tzRef = useRef(null);

  useEffect(() => {
    if (!tzOpen) return;
    const handleClickOutside = (event) => {
      if (tzRef.current && !tzRef.current.contains(event.target)) {
        setTzOpen(false);
        onBlur('raceTimezone');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tzOpen]);

  const selectTimezone = (value) => {
    handleInputChange({ target: { name: 'raceTimezone', value, type: 'select' } });
    setTzOpen(false);
    onBlur('raceTimezone');
  };

  const selectedTzLabel = TIMEZONE_OPTIONS.find((opt) => opt.value === formData?.raceTimezone)?.label;

  return (
    <div style={{
      backgroundColor: colors.surface,
      padding: '24px',
      borderRadius: '12px',
      marginBottom: '24px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      position: 'relative'
    }}>
      <h3 style={{
        color: colors.text.primary,
        fontSize: '1.2rem',
        fontWeight: '700',
        marginBottom: '20px',
        paddingBottom: '10px',
        borderBottom: `2px solid ${colors.primary}`
      }}>Race Information</h3>
      
      <style>
        {`
          .race-info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            column-gap: 24px;
            row-gap: 24px;
            margin-bottom: 20px;
            position: relative;
          }

          .race-info-input {
            width: 100%;
            max-width: 300px;
          }

          .race-start-time-input {
            max-width: 135px;
          }

          input[type="date"].race-info-input,
          input[type="time"].race-info-input {
            -webkit-appearance: none;
            appearance: none;
            padding-right: 30px;
          }

          @media (max-width: 768px) {
            .race-info-grid {
              grid-template-columns: 1fr;
              gap: 24px;
              padding: 0;
            }

            .race-info-input {
              max-width: none;
              width: 100%;
              height: 48px; /* Larger touch target on mobile */
              font-size: 16px; /* Prevent iOS zoom */
            }

            .input-container {
              width: 100%;
              margin-bottom: 8px;
            }

            input[type="date"].race-info-input,
            input[type="time"].race-info-input {
              width: 100%;
              padding: 12px 16px;
              min-height: 48px;
            }

            .input-container-right {
              padding-right: 0 !important;
            }

            /* Improve label readability on mobile */
            .input-container label {
              margin-bottom: 12px !important;
              font-size: 1.1rem !important;
            }
          }

          .gold-ring-logo {
            position: absolute;
            right: 120px;
            top: 50%;
            transform: translateY(-50%);
            width: 150px;
            height: auto;
            opacity: 0.8;
          }

          @media (max-width: 1024px) {
            .gold-ring-logo {
              width: 120px;
              right: 80px;
            }
          }

          @media (max-width: 768px) {
            .gold-ring-logo {
              width: 100px;
              right: 40px;
              opacity: 0.4;
            }
          }

          @media (max-width: 640px) {
            .gold-ring-logo {
              display: none;
            }
          }
        `}
      </style>
      
      <div className="race-info-grid">
        <div className="input-container">
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: colors.text.primary,
            fontSize: '1rem',
            fontWeight: '700'
          }}>
            Race Name
          </label>
          <input
            type="text"
            name="raceName"
            value={formData?.raceName || ''}
            onChange={handleInputChange}
            placeholder="Enter race name"
            style={{
              ...commonStyles.input
            }}
            className="race-info-input"
          />
        </div>

        <div className="input-container input-container-right" style={{ paddingRight: '250px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: colors.text.primary,
            fontSize: '1rem',
            fontWeight: '700'
          }}>
            Race Date *
          </label>
          <input
            type="date"
            name="raceDate"
            value={formData?.raceDate || ''}
            onChange={handleInputChange}
            onBlur={() => onBlur('raceDate')}
            required
            style={{
              ...commonStyles.input
            }}
            className="race-info-input"
          />
        </div>

        <div className="input-container">
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: colors.text.primary,
            fontSize: '1rem',
            fontWeight: '700'
          }}>
            Race Start Time *
          </label>
          <input
            type="time"
            name="raceStartTime"
            value={formData?.raceStartTime || ''}
            onChange={handleInputChange}
            onBlur={() => onBlur('raceStartTime')}
            required
            style={{
              ...commonStyles.input
            }}
            className="race-info-input"
          />
        </div>

        <div className="input-container input-container-right" style={{ paddingRight: '250px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: colors.text.primary,
            fontSize: '1rem',
            fontWeight: '700'
          }}>
            Race Timezone *
          </label>
          <div ref={tzRef} style={{ position: 'relative', width: '100%', maxWidth: '300px' }} className="race-tz-dropdown">
            <button
              type="button"
              onClick={() => setTzOpen((prev) => !prev)}
              title={TIMEZONE_LABELS[formData?.raceTimezone] || 'Race Timezone'}
              style={{
                ...commonStyles.input,
                width: '100%',
                textAlign: 'left',
                background: colors.surface,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span style={{ color: selectedTzLabel ? colors.text.primary : colors.text.light }}>
                {selectedTzLabel || 'Select timezone'}
              </span>
              <span style={{ fontSize: '12px', marginLeft: '8px', color: colors.text.secondary }}>{tzOpen ? '▲' : '▼'}</span>
            </button>

            {tzOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 20,
                  overflow: 'hidden'
                }}
              >
                {TIMEZONE_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => selectTimezone(opt.value)}
                    style={{
                      padding: '10px 16px',
                      cursor: 'pointer',
                      backgroundColor: formData?.raceTimezone === opt.value ? `${colors.primary}15` : 'transparent',
                      fontWeight: formData?.raceTimezone === opt.value ? '700' : '400'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.primary}10`; }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = formData?.raceTimezone === opt.value ? `${colors.primary}15` : 'transparent';
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}

            {/* Hidden input so raceTimezone participates in native form data if ever read that way; validation itself is handled by the parent's getFieldError/requiredFields check, same as other fields */}
            <input type="hidden" name="raceTimezone" value={formData?.raceTimezone || ''} />
          </div>
        </div>

        <div className="input-container">
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: colors.text.primary,
            fontSize: '1rem',
            fontWeight: '700'
          }}>
            Estimated Runs Per Hour *
          </label>
          <input
            type="number"
            name="estRunsPerHour"
            value={formData?.estRunsPerHour || ''}
            onChange={handleInputChange}
            onBlur={() => onBlur('estRunsPerHour')}
            min="1"
            required
            placeholder="Enter estimated runs per hour"
            style={{
              ...commonStyles.input
            }}
            className="race-info-input"
          />
        </div>
      </div>

      <img
        src="/gold_ring_logo.png"
        alt="Gold Ring"
        className="gold-ring-logo"
      />
    </div>
  );
};

export default RaceInformation; 